/**
 * Blue nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const blueNailDesignsPack: ToilePack = {
    slug: 'blue-nail-designs',
    category: 'beauty',
    name: 'Blue nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'baby', label: 'Baby blue', subject: 'short almond nails in a soft baby blue with a glossy finish'},
        {key: 'navy', label: 'Navy and silver', subject: 'square nails in deep navy with a silver glitter fade at the tips'},
        {key: 'porcelain', label: 'Blue porcelain', subject: 'oval nails in white with cobalt blue painted flowers and scrolls like blue-and-white china'},
    ],
    styles: [
        {key: 'salon', label: 'Salon close-up', style: 'nail salon close-up in soft diffuse daylight from camera-left, a gentle shadow under the fingers, a bright airy balance', palette: 'White and pale grey ground, the blue carried on the nails', framing: 'the hand resting flat on white marble with faint grey veins, fingertips toward the top of a vertical frame'},
        {key: 'denim', label: 'Denim day', style: 'casual lifestyle photo in bright afternoon sun from camera-right, crisp short shadows, a sunny mid-tone balance', palette: 'Faded denim and warm sunlight around the hand, the nails the strongest blue', framing: 'the hand resting on the knee of faded light-wash jeans, the background softly out of focus'},
        {key: 'silk', label: 'Grey silk', style: 'beauty editorial in soft light from above, shadow gathering in the folds, a cool low balance with the nails the brightest point', palette: 'Pale silver-grey silk all around, the nails cool and clear against it', framing: 'the hand laid across draped pale grey silk filling the frame'},
    ],
    directions: ['ocean waves', 'matte navy', 'toe nails'],
    directionHint: 'try: ocean waves',
    seo: {
        title: 'Blue Nail Designs: Baby Blue, Navy, Porcelain',
        metaDescription: 'Nine blue nail designs: a soft baby blue, navy with a silver glitter fade and cobalt porcelain flowers, in three settings.',
        h1: 'Blue nail designs: baby blue, navy with silver and blue porcelain',
        tagline: 'Nine blue sets, from pale sky to deep navy: three designs, each shown close up, on denim and on grey silk.',
        intro: 'Blue nail designs for people who want blue nails, from pale sky to deep navy. The three designs are short almond nails in a soft baby blue with a glossy finish, square nails in deep navy with a silver glitter fade at the tips, and oval white nails with cobalt flowers and scrolls like blue-and-white china. Type a different blue or a painted pattern and the same blue family carries it.',
        useCases: [
            'a pale blue for spring',
            'a navy and silver set for a night out',
            'a porcelain pattern for a summer dress',
            'a navy set for work',
        ],
        faq: [
            {q: 'What is the blue porcelain design?', a: 'Oval white nails with cobalt blue painted flowers and scrolls, in the look of blue-and-white china.'},
            {q: 'Is the navy design plain?', a: 'No. Deep navy has a silver glitter fade at the tips.'},
            {q: 'Can I ask for another blue?', a: 'Yes. A shade you type is a request, not one of the nine pictures shown.'},
        ],
    },
}
